import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './RetainerCreditsPanel.css';

const CHAT_API_BASE = '/api/chat';

const authFetch = async (url, opts = {}) => {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } });
};

const ACCOUNTS = [
  { key: 'retainer', legend: 'Retainers' },
  { key: 'credit',   legend: 'Credits' },
  { key: 'srisd',    legend: 'SRISD' },
];

const fmt = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signed = (n) => (Number(n) < 0 ? '-' : '') + fmt(Math.abs(Number(n) || 0));
// Local calendar date (YYYY-MM-DD). Avoids the UTC off-by-one that
// toISOString() causes for users behind UTC.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Parse date-only values (YYYY-MM-DD) as LOCAL, not UTC, so "2026-08-21"
// doesn't render as 8/20 in timezones behind UTC.
const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString();
  return new Date(s.replace(' ', 'T')).toLocaleDateString();
};
const emptyForm = () => ({ amount: '', description: '', transaction_date: today() });
const currentUserId = () => {
  try { return (JSON.parse(sessionStorage.getItem('user') || 'null') || {}).id || null; }
  catch (e) { return null; }
};

// Retainer / Credit / SRISD accounts for a project. One running-balance account
// per category: deposits (manual or via Stripe) add to the balance; payments
// recorded against invoices/proposals with that method draw it down.
const RetainerCreditsPanel = ({ projectId }) => {
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripeReady, setStripeReady] = useState(false);
  const [forms, setForms] = useState({});   // per-type deposit form
  const [busy, setBusy] = useState({});      // per-type busy flag
  const [errors, setErrors] = useState({});  // per-type error message

  const load = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    try {
      const res = await authFetch(`${CHAT_API_BASE}/rcs.php?project_id=${encodeURIComponent(projectId)}`);
      const data = await res.json();
      if (data.success) setAccounts(data.accounts);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // Is Stripe connected? Gates the "Deposit via Stripe" button.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${CHAT_API_BASE}/stripe-status.php`);
        const d = await r.json();
        if (alive) setStripeReady(!!(d && (d.connected || (d.data && d.data.connected))));
      } catch (e) { /* leave disabled */ }
    })();
    return () => { alive = false; };
  }, []);

  // A Stripe deposit completes in another tab; refresh balances on return.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const getForm = (type) => forms[type] || emptyForm();
  const setForm = (type, patch) => setForms(f => ({ ...f, [type]: { ...(f[type] || emptyForm()), ...patch } }));
  const setErr = (type, msg) => setErrors(e => ({ ...e, [type]: msg }));

  const recordDeposit = async (type) => {
    const form = getForm(type);
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setErr(type, 'Enter a deposit amount.'); return; }
    setBusy(b => ({ ...b, [type]: true })); setErr(type, '');
    try {
      const res = await authFetch(`${CHAT_API_BASE}/rcs.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId, account_type: type,
          amount: Math.round(amt * 100) / 100,
          description: form.description.trim(),
          transaction_date: form.transaction_date,
          user_id: currentUserId(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setForms(f => ({ ...f, [type]: emptyForm() }));
        if (data.accounts) setAccounts(data.accounts); else load();
      } else {
        setErr(type, data.message || 'Failed to record deposit');
      }
    } catch (e) {
      setErr(type, 'Failed to record deposit: ' + e.message);
    }
    setBusy(b => ({ ...b, [type]: false }));
  };

  const depositViaStripe = async (type) => {
    const form = getForm(type);
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setErr(type, 'Enter a deposit amount first.'); return; }
    setBusy(b => ({ ...b, [type]: true })); setErr(type, '');
    try {
      const res = await authFetch(`${CHAT_API_BASE}/rcs-deposit-link.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId, account_type: type,
          amount: Math.round(amt * 100) / 100,
          user_id: currentUserId(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.url) {
        window.open(data.url, '_blank', 'noopener');
        setErr(type, 'Stripe checkout opened in a new tab — the balance updates once payment clears.');
      } else {
        setErr(type, data.message || 'Failed to start Stripe deposit');
      }
    } catch (e) {
      setErr(type, 'Failed to start Stripe deposit: ' + e.message);
    }
    setBusy(b => ({ ...b, [type]: false }));
  };

  const removeTxn = async (txn) => {
    if (txn.entry_type === 'application') {
      alert('This entry came from a payment. Delete the payment on the invoice/proposal to reverse it.');
      return;
    }
    if (!window.confirm('Delete this deposit?')) return;
    try {
      const res = await authFetch(`${CHAT_API_BASE}/rcs.php?id=${encodeURIComponent(txn.id)}&user_id=${encodeURIComponent(currentUserId() || '')}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) { if (data.accounts) setAccounts(data.accounts); else load(); }
      else alert(data.message || 'Failed to delete');
    } catch (e) { alert('Failed to delete: ' + e.message); }
  };

  if (loading) return <div className="rcs-section"><p className="rcs-loading">Loading accounts…</p></div>;

  return (
    <div className="rcs-section">
      {ACCOUNTS.map(({ key, legend }) => {
        const acct = (accounts && accounts[key]) || { balance: 0, transactions: [] };
        const txns = acct.transactions || [];
        const form = getForm(key);
        const isBusy = !!busy[key];
        return (
          <fieldset key={key} className="rcs-fieldset">
            <legend className="rcs-legend">{legend}</legend>

            <div className="rcs-balance-row">
              <span className="rcs-balance-label">Balance</span>
              <span className={`rcs-balance-amt ${Number(acct.balance) > 0 ? 'pos' : ''}`}>{fmt(acct.balance)}</span>
            </div>

            <table className="rcs-table">
              <thead>
                <tr><th>Date</th><th>Source</th><th>Description</th><th className="rcs-num">Amount</th><th className="rcs-actions"></th></tr>
              </thead>
              <tbody>
                {txns.length === 0 ? (
                  <tr className="rcs-empty"><td colSpan={5}>No {legend.toLowerCase()} yet.</td></tr>
                ) : txns.map(t => (
                  <tr key={t.id} className={t.status === 'pending' ? 'rcs-pending' : ''}>
                    <td>{fmtDate(t.transaction_date || t.date_entered)}</td>
                    <td className="rcs-source">
                      {(() => {
                        // Draw-downs: what the credit was applied to.
                        if (t.entry_type === 'application' && t.parent_type) {
                          const parentLabel = t.parent_type === 'quote'          ? 'Proposal'
                                            : t.parent_type === 'purchase_order' ? 'PO'
                                            :                                      'Invoice';
                          return `${parentLabel}${t.parent_number ? ' #' + t.parent_number : ''}`;
                        }
                        // Deposits staged from an invoice/proposal: show that doc.
                        if (t.source_parent_type === 'AOS_Invoices' || t.source_parent_type === 'AOS_Quotes') {
                          const label = t.source_parent_type === 'AOS_Quotes' ? 'Proposal' : 'Invoice';
                          return `${label}${t.source_number ? ' #' + t.source_number : ''}`;
                        }
                        return '';
                      })()}
                    </td>
                    <td>
                      {t.description || (t.entry_type === 'application' ? 'Payment applied' : 'Deposit')}
                      {t.source === 'stripe' && <span className="rcs-tag">Stripe</span>}
                      {t.status === 'pending' && <span className="rcs-tag rcs-tag-pending">Pending</span>}
                    </td>
                    <td className={`rcs-num ${Number(t.amount) < 0 ? 'neg' : 'pos'}`}>{signed(t.amount)}</td>
                    <td className="rcs-actions">
                      {t.entry_type !== 'application' && (
                        <button className="rcs-del" title="Delete deposit" onClick={() => removeTxn(t)}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="rcs-deposit">
              <div className="rcs-deposit-head">Record a deposit</div>
              <div className="rcs-deposit-fields">
                <label className="rcs-field rcs-field-amt">Amount
                  <input type="number" min="0" step="0.01" placeholder="0.00" value={form.amount}
                    onChange={e => setForm(key, { amount: e.target.value })} />
                </label>
                <label className="rcs-field rcs-field-date">Date
                  <input type="date" value={form.transaction_date}
                    onChange={e => setForm(key, { transaction_date: e.target.value })} />
                </label>
                <label className="rcs-field rcs-field-desc">Description
                  <input type="text" placeholder="Reference, check #, note…" value={form.description}
                    onChange={e => setForm(key, { description: e.target.value })} />
                </label>
              </div>
              {errors[key] && <div className="rcs-error">{errors[key]}</div>}
              <div className="rcs-deposit-actions">
                <button className="rcs-btn" onClick={() => recordDeposit(key)} disabled={isBusy || !form.amount}>
                  {isBusy ? 'Saving…' : 'Record deposit'}
                </button>
                {stripeReady && (
                  <button className="rcs-btn rcs-btn-stripe" onClick={() => depositViaStripe(key)} disabled={isBusy || !form.amount}>
                    💳 Deposit via Stripe
                  </button>
                )}
              </div>
            </div>
          </fieldset>
        );
      })}
    </div>
  );
};

export default RetainerCreditsPanel;
